import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Trophy, ShieldPlus } from "lucide-react";
import PlayerAvatar from "../../components/football/PlayerAvatar";
import { useFb } from "../../utils/football";

const FINISHED = ["FT", "AET", "PEN"];
const LIVE = ["1H", "HT", "2H", "ET", "BT", "P", "SUSP", "INT", "LIVE"];

const Section = ({ title, children, testid }) => (
  <div className="mt-4 rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </div>
);

const PlayerLine = ({ p, right }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex min-w-0 items-center gap-2">
      <PlayerAvatar player={p} size={32} />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{p.name}</div>
        <div className="text-[11px] text-muted-foreground">{p.position}</div>
      </div>
    </div>
    {right != null && <span className="text-sm font-extrabold wc-gold">{right}</span>}
  </div>
);

// Match events timeline (goals / cards / substitutions) with minutes.
const EventRow = ({ ev }) => {
  const min = ev.minute != null ? `${ev.minute}'` : "";
  let icon = "•";
  let text = ev.player_name || "Unknown";
  if (ev.type === "Goal") {
    icon = ev.detail === "Own Goal" ? "⚽" : "⚽";
    text = ev.detail === "Own Goal"
      ? `${ev.player_name} (OG)`
      : `${ev.player_name}${ev.assist_name ? ` (assist: ${ev.assist_name})` : ""}`;
  } else if (ev.type === "Card") {
    icon = ev.detail === "Red Card" ? "🟥" : "🟨";
    text = ev.player_name;
  } else if ((ev.type || "").toLowerCase() === "subst") {
    icon = "🔄";
    text = `${ev.assist_name || "—"} off → ${ev.player_name || "—"} on`;
  } else {
    return null;
  }
  return (
    <div className="flex items-center gap-2 py-1.5 text-sm" data-testid="fixture-event-row">
      <span className="w-9 shrink-0 text-right text-xs font-bold text-muted-foreground">{min}</span>
      <span className="shrink-0">{icon}</span>
      <span className="truncate font-semibold">{text}</span>
    </div>
  );
};

export default function FootballFixtureDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fb = useFb();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer;
    let cancelled = false;
    const load = async () => {
      try {
        const d = await fb.get(`/football/fixtures/${id}/detail`);
        if (cancelled) return;
        setData(d);
        // Poll every 30s ONLY while the match is live (not for NS/finished fixtures).
        if (d.is_live) timer = setTimeout(load, 30000);
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <div className="football-theme min-h-screen bg-background p-4"><div className="h-60 animate-pulse rounded-2xl bg-secondary/50" /></div>;
  }
  if (!data?.fixture) {
    return <div className="football-theme min-h-screen bg-background p-6 text-center text-sm text-muted-foreground">Fixture not found.</div>;
  }

  const fx = data.fixture;
  const live = LIVE.includes(fx.status);
  const finished = FINISHED.includes(fx.status);
  const pre = !live && !finished;
  const showScore = live || finished;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="fixture-detail-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-6 pt-4">
        <button onClick={() => navigate(-1)} className="mb-3 inline-flex items-center gap-1 text-sm text-white/80" data-testid="fixture-back"><ChevronLeft size={18} /> Back</button>
        {fx.stage && <div className="mb-1 text-center text-xs font-bold text-amber-200">{fx.stage}</div>}
        <div className="flex items-center justify-around text-white">
          <div className="flex flex-col items-center gap-1" data-testid="fixture-home">
            {fx.home_team?.logo_url && <img src={fx.home_team.logo_url} alt="" className="h-10 w-10 object-contain" />}
            <span className="max-w-[90px] truncate text-sm font-bold">{fx.home_team?.name || "TBD"}</span>
          </div>
          <div className="text-center">
            {showScore ? (
              <div className="text-3xl font-extrabold" data-testid="fixture-score">{fx.home_score ?? 0} - {fx.away_score ?? 0}</div>
            ) : (
              <div className="text-sm font-bold text-amber-200">{fx.kickoff_utc ? new Date(fx.kickoff_utc).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "TBD"}</div>
            )}
            {live && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-500/30 px-2 py-0.5 text-[10px] font-extrabold"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" /> LIVE</div>}
            {finished && <div className="mt-1 text-[11px] font-bold text-white/70">Full Time</div>}
          </div>
          <div className="flex flex-col items-center gap-1" data-testid="fixture-away">
            {fx.away_team?.logo_url && <img src={fx.away_team.logo_url} alt="" className="h-10 w-10 object-contain" />}
            <span className="max-w-[90px] truncate text-sm font-bold">{fx.away_team?.name || "TBD"}</span>
          </div>
        </div>
        {(fx.venue || fx.city) && (
          <div className="mt-3 flex items-center justify-center gap-1 text-xs text-white/70" data-testid="fixture-venue">
            <MapPin size={12} /> {[fx.venue, fx.city].filter(Boolean).join(", ")}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-md px-4">
        {/* Build CTA (pre-match) */}
        {pre && (
          <button onClick={() => navigate(`/football/squad-builder/${fx.match_day}`)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-3 text-sm font-extrabold text-slate-900" data-testid="fixture-build-cta">
            <ShieldPlus size={16} /> Build your squad for this match day
          </button>
        )}

        {/* Match events timeline (prominent) — goals, cards, substitutions with minutes */}
        {showScore && data.events?.length > 0 && (
          <Section title="Match Events" testid="fixture-events">
            <div className="space-y-0.5">
              {data.events
                .filter((ev) => ["Goal", "Card", "subst"].includes(ev.type) || (ev.type || "").toLowerCase() === "subst")
                .map((ev, i) => <EventRow key={i} ev={ev} />)}
            </div>
          </Section>
        )}

        {/* Lineups */}
        {data.lineups_available && (data.starting_xi.home.length > 0 || data.starting_xi.away.length > 0) ? (
          <Section title="Starting XI" testid="fixture-lineups">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-1 text-xs font-bold">{fx.home_team?.name}</div>
                {data.starting_xi.home.map((p) => <PlayerLine key={p.player_id} p={p} />)}
              </div>
              <div>
                <div className="mb-1 text-xs font-bold">{fx.away_team?.name}</div>
                {data.starting_xi.away.map((p) => <PlayerLine key={p.player_id} p={p} />)}
              </div>
            </div>
          </Section>
        ) : pre ? (
          <Section title="Lineups" testid="fixture-lineups-pending">
            <p className="text-sm text-muted-foreground">Lineups not yet available — published around 45 minutes before kickoff.</p>
          </Section>
        ) : null}

        {/* Post / live results */}
        {showScore && (
          <>
            {data.top_performers?.length > 0 && (
              <Section title="Top Performers" testid="fixture-top-performers">
                {data.top_performers.map((p) => <PlayerLine key={p.player_id} p={p} right={`${p.fantasy_points} pts`} />)}
              </Section>
            )}
            {data.scorers?.length > 0 && (
              <Section title="Goal Scorers" testid="fixture-scorers">
                {data.scorers.map((p) => <PlayerLine key={p.player_id} p={p} right={`${p.goals} ⚽`} />)}
              </Section>
            )}
            {data.assists?.length > 0 && (
              <Section title="Assists" testid="fixture-assists">
                {data.assists.map((p) => <PlayerLine key={p.player_id} p={p} right={`${p.assists} 🅰️`} />)}
              </Section>
            )}
            {data.cards?.length > 0 && (
              <Section title="Cards" testid="fixture-cards">
                {data.cards.map((p) => <PlayerLine key={p.player_id} p={p} right={`${p.red_cards ? "🟥" : ""}${p.yellow_cards ? "🟨".repeat(p.yellow_cards) : ""}`} />)}
              </Section>
            )}
            {data.user_has_locked_squad && finished && (
              <button onClick={() => navigate(`/football/results/${fx.match_day}`)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 py-3 text-sm font-extrabold wc-gold" data-testid="fixture-my-squad-cta">
                <Trophy size={16} /> View your squad's performance
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
